import { Portal, Box, Text, HStack } from '@chakra-ui/core';
import React, { memo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { formElementSchema } from '../schemas/formElement.schema';

const PREVIEW_BOX_STYLES = {
  position: 'fixed' as const,
  top: 0,
  left: 0,
  pointerEvents: 'none' as const,
  zIndex: 9999,
  bg: 'rgba(255, 255, 255, 1)',
  borderRadius: 'xl',
  border: '1px solid',
  borderColor: 'blue.200',
  boxShadow: '0 8px 20px 0 rgba(31, 38, 135, 0.15)',
  p: 4,
  minW: '200px',
  transform: 'scale(1.05)',
};

const ICON_BOX_PROPS = {
  p: 2,
  borderRadius: 'md' as const,
};

const TEXT_PROPS = {
  fontSize: 'sm' as const,
  fontWeight: 'semibold' as const,
  color: 'gray.800',
};

/**
 * A portal that displays a preview of the element being dragged, following the cursor.
 * It is rendered outside of the normal DOM hierarchy to avoid being clipped by parent containers.
 * This component subscribes to the questionnaire template store to get information about the dragged element.
 */
export const DragPreviewPortal = memo(function DragPreviewPortal() {
  const { t } = useTranslation('questionnaireTemplate');
  const previewRef = useRef<HTMLDivElement>(null);

  const dragInfo = QuestionnaireTemplateStore.useStoreState(
    useCallback((state) => {
      const { dragSource } = state.ui;
      if (!dragSource) return null;

      const draggedId = state.ui.draggedElementId;
      const isNew = dragSource.type === 'toolbox';
      const { templateType } = dragSource;
      const element = formElementSchema[templateType];

      const label = isNew ? element.label : state.entities.elements[draggedId!]?.label || 'Moving Element...';

      return {
        label,
        icon: element.icon,
        colors: element.colors,
        isDragging: true,
      };
    }, []),
  );

  // Memoized mouse move handler
  const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
    /* istanbul ignore else */
    if (previewRef.current) {
      previewRef.current.style.opacity = '0.9';
      previewRef.current.style.transform = `translate3d(${e.clientX + 10}px, ${e.clientY + 10}px, 0)`;
    }
  }, []);

  // This effect handles the movement of the drag preview.
  // It directly manipulates the DOM for performance reasons, avoiding React re-renders on every mouse move.
  useEffect(() => {
    if (!dragInfo) return;

    window.addEventListener('pointermove', handleGlobalMouseMove);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('pointermove', handleGlobalMouseMove);
  }, [dragInfo, handleGlobalMouseMove]);

  if (!dragInfo) return null;

  const { label, icon: Icon, colors } = dragInfo;

  return (
    <Portal>
      <Box
        data-testid="drag-preview"
        ref={previewRef}
        {...PREVIEW_BOX_STYLES}
        sx={{ backdropFilter: 'blur(8px) saturate(180%)' }}
      >
        <HStack spacing={3}>
          <Box {...ICON_BOX_PROPS} bg={colors.bg}>
            <Icon name="drag-handle" color={colors.text} data-testid="drag-preview-icon" />
          </Box>
          <Text {...TEXT_PROPS}>{t(`formBuilder.schema.${label}`)}</Text>
        </HStack>
      </Box>
    </Portal>
  );
});
